import { Axios } from "@/apis/axios";
import {
  Box,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
} from "@chakra-ui/react";
import {
  ColorIcon,
  colors,
  customEvent,
  Field,
  fireLeadSubmitEvents,
  formatPhoneNumber,
  getCookie,
  HStack,
  isValidPhoneNumber,
  queryKeys,
  VStack,
} from "goi_common";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { useQuery } from "react-query";

const STATUS_OPTIONS = [
  "미리 준비하려고 해요",
  "임종이 임박하신 상태입니다.",
  "임종하신 상태입니다.",
] as const;

const checklistLeadStatus = (memo: string) => {
  if (memo === "미리 준비하려고 해요") return "여유";
  if (memo === "임종이 임박하신 상태입니다.") return "임박";
  if (memo === "임종하신 상태입니다.") return "임종상태";
  return undefined;
};

const useGetServerTime = () =>
  useQuery<{ timestamp: Date; is_night: boolean }>(
    queryKeys.nightTime(),
    async () => {
      const response = await Axios.get("/now_time/");
      return response.data;
    }
  );

interface DirectionsChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  parlorId: number;
  /**
   * 이벤트 path. 기본값은 directions 것이라 기존 데이터가 안 끊긴다.
   * 무빈소 페이지는 "prlr_mubinso", 임종 안내 랜딩은 "prlr_urgent" 를 넘긴다.
   * 모든 path 가 i_checklist_* trigger 를 갖고 있어야 한다.
   */
  eventPath?: "prlr_directions" | "prlr_mubinso" | "prlr_urgent";
  /**
   * 설명 문구. 기본값은 directions·mubinso 가 쓰던 것 그대로라 기존 화면은 영향 없다.
   * 임종 안내 랜딩만 FAB 라벨과 말을 맞추려고 "임종 상황 체크리스트" 로 넘긴다.
   */
  description?: string;
}

export const DirectionsChecklistModal = ({
  isOpen,
  onClose,
  parlorId,
  eventPath = "prlr_directions",
  description = "장례 비용 예상 견적표, 임종 준비 체크리스트를 제공해드리고 있습니다.",
}: DirectionsChecklistModalProps) => {
  const [value, setValue] = useState("");
  const [memo, setMemo] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  const { data: serverTime } = useGetServerTime();
  const isNight = serverTime?.is_night ?? false;
  const router = useRouter();

  useEffect(() => {
    if (!isOpen) setIsCompleted(false);
  }, [isOpen]);

  const track = (
    trigger:
      | "i_checklist_submit"
      | "i_checklist_success"
      | "i_checklist_close"
      | "i_checklist_agree"
      | "i_checklist_no"
  ) =>
    customEvent({
      newGtm: {
        path: eventPath,
        trigger_and_target: trigger,
        properties: { parlor_id: parlorId },
      },
    });

  const handleSubmit = useCallback(() => {
    track("i_checklist_submit");

    if (!value) {
      alert("번호를 입력해주세요.");
      return;
    }
    if (!memo) {
      alert("장례 준비 상황을 알려주세요.");
      return;
    }
    if (!isValidPhoneNumber(value)) {
      alert("번호 형식을 확인해주세요. (010-****-****)");
      return;
    }

    Axios.post("/v2/leads/", {
      phone: value,
      lead_type: "checklist",
      slack_message: `*페이지* ${window.location.origin}${
        router.asPath
      }\n*리퍼러* ${getCookie("referrer") ?? ""}`,
      status: checklistLeadStatus(memo),
      accept_night_call: isNight ? true : undefined,
    })
      .then(() => {
        // platform 은 useSubmitLead 훅에서 발화. parlor 는 래퍼가 없어 여기서 직접 부른다.
        fireLeadSubmitEvents("checklist", checklistLeadStatus(memo));
        track("i_checklist_success");
        setIsCompleted(true);
      })
      .catch(() => alert("번호를 확인해주세요."));
  }, [value, memo, router.asPath, isNight]);

  const handleMarketingAgree = useCallback(() => {
    track("i_checklist_agree");
    Axios.post("/lead_consent_records/", {
      phone: value,
      agreement: { marketing_agreed: true },
    }).catch((error) => console.error("마케팅 수신동의 기록 실패:", error));
    onClose();
  }, [value, onClose]);

  const handleClose = useCallback(() => {
    track(isCompleted ? "i_checklist_no" : "i_checklist_close");
    onClose();
  }, [isCompleted, onClose]);

  return (
    <Box height="100%" width="100%">
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        closeOnOverlayClick={false}
        scrollBehavior="inside"
        isCentered
        useInert={false}
      >
        <ModalOverlay width="100%" height="100%" />
        <ModalContent
          minWidth="320px"
          backgroundColor={colors.white}
          backgroundSize="cover"
        >
          <ModalBody padding="20px 24px 44px">
            {isCompleted ? (
              <VStack width="100%" gap={24}>
                <HStack width="100%" justifyContent="flex-end">
                  <button type="button" onClick={handleClose} aria-label="닫기">
                    <ColorIcon
                      name="close"
                      width={28}
                      height={28}
                      color={colors.black}
                    />
                  </button>
                </HStack>
                <VStack width="100%" gap={12} alignItems="center">
                  <Box className="subtitle_21_b gray900" textAlign="center">
                    신청이 완료되었어요
                  </Box>
                  <Box className="caption_12_b gray700" textAlign="center">
                    마케팅 정보 수신에 동의하시면
                    <br />
                    장례 관련 혜택, 이벤트 소식을 보내드려요.
                  </Box>
                </VStack>
                <HStack
                  width="100%"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <HStack alignItems="center" gap={4}>
                    <Box className="caption_11_b" color={colors.gray400}>
                      선택
                    </Box>
                    <Box className="caption_11_b gray700">
                      마케팅 정보 수신 동의
                    </Box>
                  </HStack>
                  <a
                    className="caption_11_b"
                    href="https://goifuneral.notion.site/3d74caff865d48ccbf32d9a0ad3df80b"
                    target="_blank"
                    rel="noreferrer"
                  >
                    보기
                  </a>
                </HStack>
                <HStack width="100%" gap={8}>
                  <Button
                    flex={1}
                    backgroundColor={colors.gray100}
                    color={colors.gray700}
                    onClick={handleClose}
                  >
                    닫기
                  </Button>
                  <Button
                    flex={1}
                    backgroundColor={colors.orange650}
                    color={colors.white}
                    onClick={handleMarketingAgree}
                  >
                    동의하기
                  </Button>
                </HStack>
              </VStack>
            ) : (
              <VStack width="100%" gap={10}>
                <HStack width="100%" justifyContent="end" alignItems="center">
                  <HStack width="100%" alignItems="center" gap={8}>
                    <ColorIcon
                      name="list"
                      color={colors.gray900}
                      width={16}
                      height={16}
                    />
                    <Box className="subtitle_21_b gray900">체크리스트 받기</Box>
                  </HStack>
                  <button type="button" onClick={handleClose} aria-label="닫기">
                    <ColorIcon
                      name="close"
                      width={28}
                      height={28}
                      color={colors.black}
                    />
                  </button>
                </HStack>
                <VStack width="100%" gap={24}>
                  <Box className="caption_12_b gray800">{description}</Box>
                  <Input
                    value={value}
                    onChange={(event) =>
                      setValue(formatPhoneNumber(event.target.value))
                    }
                    maxLength={13}
                    type="tel"
                    border={`1px solid ${colors.gray400}`}
                    width="100%"
                    placeholder="010-****-****"
                  />
                  <Box width="100%">
                    <Box className="gray700 subtitle_14_b" pb="12px">
                      상황을 알려주세요(맞춤형 안내)
                    </Box>
                    <Field.Radio
                      title=""
                      items={[...STATUS_OPTIONS]}
                      value={memo}
                      onChange={setMemo}
                      radioWidth="100%"
                      className="body_14_r"
                      flexDirection="column"
                      alignItem="center"
                    />
                  </Box>
                  <VStack width="100%" gap={8}>
                    <HStack alignItems="center" justifyContent="space-between">
                      <HStack alignItems="center" gap={4}>
                        <Box className="caption_11_b" color={colors.gray400}>
                          필수
                        </Box>
                        <Box className="caption_11_b gray700">
                          개인정보 수집, 이용, 제공 동의
                        </Box>
                      </HStack>
                      <a
                        className="caption_11_b"
                        href="https://goifuneral.notion.site/146a79013e6c40a9b4731dd07a8ad99d"
                        target="_blank"
                        rel="noreferrer"
                      >
                        보기
                      </a>
                    </HStack>
                    {isNight && (
                      <HStack
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <HStack alignItems="center" gap={4}>
                          <Box className="caption_11_b" color={colors.gray400}>
                            필수
                          </Box>
                          <Box className="caption_11_b gray700">
                            긴급 상황 시 수신 동의(야간)
                          </Box>
                        </HStack>
                        <a
                          className="caption_11_b"
                          href="https://goifuneral.notion.site/3e893cfdb8584fb28a4086a60aef5ad4"
                          target="_blank"
                          rel="noreferrer"
                        >
                          보기
                        </a>
                      </HStack>
                    )}
                  </VStack>
                  <Button
                    width="100%"
                    backgroundColor={colors.orange650}
                    color={colors.white}
                    onClick={handleSubmit}
                  >
                    동의하고 자료 받아보기
                  </Button>
                </VStack>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
